// Builds the GeoJSON each map layer renders from app state. Pure functions so
// they can be unit tested and memoized.
import type { Feature, FeatureCollection, Point, Polygon, MultiPolygon } from 'geojson';
import { ALERT_STYLE } from '../config/alerts';
import { categoryColor } from '../config/gauges';
import { ensureMinimumBrightness } from '../lib/color';
import type { Alert } from '../types/alerts';
import { gaugeIsFlooding, type Gauge } from '../types/gauges';
import type { SearchPin } from '../types/search';
import type { Station } from '../types/stations';

export const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

export interface AlertPolygonProps {
  id: string;
  kind: string;
  color: string;
}

export function alertPolygonsFC(alerts: Alert[]): FeatureCollection<Polygon | MultiPolygon, AlertPolygonProps> {
  const features: Feature<Polygon | MultiPolygon, AlertPolygonProps>[] = [];
  for (const a of alerts) {
    if (!a.geometry) continue;
    features.push({
      type: 'Feature',
      id: a.id,
      geometry: a.geometry,
      properties: { id: a.id, kind: a.kind, color: ALERT_STYLE[a.kind].color },
    });
  }
  return { type: 'FeatureCollection', features };
}

export interface AlertPointProps {
  id: string;
  kind: string;
  priority: number;
}

/** One point per alert at the iOS centroid (mean of all vertices). */
export function alertPointsFC(alerts: Alert[]): FeatureCollection<Point, AlertPointProps> {
  const features: Feature<Point, AlertPointProps>[] = [];
  for (const a of alerts) {
    if (!a.centroid) continue;
    features.push({
      type: 'Feature',
      id: a.id,
      geometry: { type: 'Point', coordinates: [a.centroid.lng, a.centroid.lat] },
      properties: { id: a.id, kind: a.kind, priority: ALERT_STYLE[a.kind].priority },
    });
  }
  return { type: 'FeatureCollection', features };
}

export interface GaugePointProps {
  id: string;
  name: string;
  color: string;
  isFlooding: 0 | 1;
  /** "Nh" hours to crest while rising, else "". */
  crestLabel: string;
  crestColor: string;
  /** "Nh ↑" or "↓" style next-stage label, else "". */
  nextLabel: string;
  nextColor: string;
}

const UNKNOWN_FORECAST_COLOR = '#1a73e8';

/** GaugePinView inputs, precomputed per gauge. */
export function gaugePointsFC(gauges: Gauge[], includeNonFlooding: boolean): FeatureCollection<Point, GaugePointProps> {
  const features: Feature<Point, GaugePointProps>[] = [];
  for (const g of gauges) {
    const flooding = gaugeIsFlooding(g);
    if (!includeNonFlooding && !flooding) continue;
    const forecastColor =
      g.forecastSeverity && g.forecastSeverity !== 'unknown' ? categoryColor(g.forecastSeverity) : UNKNOWN_FORECAST_COLOR;
    const crestLabel = g.forecastIsRising === true && g.hoursToCrest != null ? `${g.hoursToCrest}h` : '';
    let nextLabel = '';
    if (g.forecastIsRising != null) {
      const hours = g.hoursToNextStage != null ? `${g.hoursToNextStage}h ` : '';
      nextLabel = `${hours}${g.forecastIsRising ? '↑' : '↓'}`;
    }
    features.push({
      type: 'Feature',
      id: g.id,
      geometry: { type: 'Point', coordinates: [g.lng, g.lat] },
      properties: {
        id: g.id,
        name: g.name,
        color: categoryColor(g.observationSeverity),
        isFlooding: flooding ? 1 : 0,
        crestLabel,
        crestColor: ensureMinimumBrightness(forecastColor, 0.85),
        nextLabel,
        nextColor: ensureMinimumBrightness(g.nextStage ? categoryColor(g.nextStage) : '#ffffff', 0.85),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function stationPointsFC(stations: Station[]): FeatureCollection<Point, { id: string; name: string }> {
  return {
    type: 'FeatureCollection',
    features: stations.map((s) => ({
      type: 'Feature',
      id: s.id,
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      properties: { id: s.id, name: s.name },
    })),
  };
}

export function searchPinsFC(pins: SearchPin[]): FeatureCollection<Point, { id: string; title: string }> {
  return {
    type: 'FeatureCollection',
    features: pins.map((p) => ({
      type: 'Feature',
      id: p.id,
      geometry: { type: 'Point', coordinates: [p.location.lng, p.location.lat] },
      properties: { id: p.id, title: p.title },
    })),
  };
}
